import type {
  FeatureBlockContent,
  FaqContent,
  HeroContent,
  IconName,
  ImageRef,
  LinkItem,
  PayrollSavingsCalculatorContent,
  TextSectionContent,
} from './types';

const businessAppBaseUrl = (
  import.meta.env.VITE_BUSINESS_APP_URL ??
  (import.meta.env.DEV ? 'http://localhost:3003' : 'https://nsave.business')
).replace(/\/$/u, '');

export const businessApplyHref = `${businessAppBaseUrl}/registration`;
export const businessCallHref = '/business/demo';
export const businessWaitlistHref = '/business/waitlist';
export const businessHowItWorksHref = '/business/how-it-works';
export const businessPayrollTermsHref = '/terms/business-payroll-terms';

export const businessHero: HeroContent = {
  title: 'Pay your team\nat 0 Fees',
  cta: { label: 'Apply for Payroll', href: businessApplyHref, external: true },
  secondaryCta: { label: 'Request a demo', href: businessCallHref },
  ctaDetail: '$200 fee, refunded after first payroll.',
  ctaDetailLink: { label: 'T&Cs apply.', href: businessPayrollTermsHref },
  ctaNote: {
    text: 'Need a general business account?',
    link: { label: 'Join the waitlist.', href: businessWaitlistHref },
  },
  image: {
    src: 'https://masref-assets.s3.eu-west-1.amazonaws.com/images/payroll/2026-10-08/hero-payroll.jpg',
    width: 2099,
    height: 1200,
    alt: 'nsave business dashboard showing a USD balance and transactions on a desktop monitor in a warm office',
  },
};

export const businessTrust: TextSectionContent = {
  title: 'A payroll account built for international teams',
  body: 'Fund one account, pay your international team and let employees receive their salary in their own nsave accounts.',
  link: { label: 'See How It Works', href: businessHowItWorksHref },
};

export interface BusinessPath {
  icon: IconName;
  title: string;
  subtitle?: string;
  description: string;
  detail?: string;
  cta: LinkItem;
}

export const businessPaths: { title: string; subtitle?: string; items: BusinessPath[] } = {
  title: 'Looking for a general business account instead?',
  items: [
    {
      icon: 'clock',
      title: 'Join the business account waitlist',
      description:
        "We're rebuilding nsave Business from the ground up, so we've paused new general accounts until the end of the year. Join the waitlist and you'll be first in line when doors reopen.",
      cta: { label: 'Join the Waitlist', href: businessWaitlistHref },
    },
  ],
};

export const businessReceive: FeatureBlockContent = {
  title: 'Reduce the cost of\ninternational payroll',
  image: {
    src: '/images/business/receive.jpg',
    alt: 'nsave multi-currency account showing USD, GBP and EUR balances beside a red notebook',
  },
  rows: [
    {
      icon: 'bank',
      title: '1. Fund your payroll account',
      description:
        'Add payroll funds to your nsave business account before your scheduled payroll run.',
    },
    {
      icon: 'users',
      title: '2. Pay your employees',
      description:
        'Your team opens a free nsave account in minutes using their email. Send salaries to each employee from one account.',
    },
    {
      icon: 'wallet',
      title: '3. Your team receives their salary',
      description:
        'Salary transfers settle instantly in your employees’ nsave accounts with $0 nsave transfer fees.',
    },
  ],
};

export const businessSavingsCalculator: PayrollSavingsCalculatorContent = {
  title: 'How much could you save on payroll?',
  description:
    'Compare what you pay today with nsave. No monthly subscription and no nsave transfer fees.',
  presetsLabel: 'Start from a typical team',
  presets: [
    { label: 'Small team', employees: 5 },
    { label: 'Growing team', employees: 25 },
    { label: 'Large team', employees: 100 },
  ],
  employeesLabel: 'Employees paid each month',
  nsaveColumnLabel: 'With nsave',
  providerColumnLabel: 'Your provider today',
  feeLabel: 'Fee per transfer',
  subscriptionLabel: 'Subscription per employee / month',
  nsaveNote: 'Payroll between nsave accounts. Always $0.',
  providerNote: 'Change these to what your current provider charges you.',
  savingsLabel: 'You could save over 12 months',
  monthlySavingsSuffix: 'every month',
  breakdownLabel: 'What you stop paying each year',
  transferFeesLabel: 'Transfer fees',
  subscriptionsLabel: 'Payroll subscriptions',
  totalLabel: 'Total saved',
  primaryCta: { label: 'Apply for Payroll', href: businessApplyHref, external: true },
  secondaryCta: { label: 'Book a demo', href: businessCallHref },
  disclaimer:
    'An estimate, not an offer. Based on one payroll run a month. nsave charges no fee on payroll transfers between nsave accounts; FX and local payout fees may apply depending on market. $200 application fee applies. T&Cs apply.',
};

export const businessGlobalTeam: FeatureBlockContent = {
  title: 'One payroll account for your global team',
  image: {
    ...businessHero.image,
    alt: 'nsave business dashboard used to manage payroll for a global team',
  },
  rows: [
    {
      icon: 'globe',
      title: 'Support international teams',
      description: 'Pay employees and contractors across supported markets.',
    },
    {
      icon: 'at-sign',
      title: 'Pay with an email or ntag',
      description: 'Use simple employee details instead of entering long bank details each month.',
    },
    {
      icon: 'wallet',
      title: 'Give employees more flexibility',
      description: 'Employees choose how to hold, spend or withdraw their salary where available.',
    },
    {
      icon: 'chart',
      title: 'Scale with your team',
      description: 'Add employees and keep payroll details together as your team grows.',
    },
  ],
};

export const businessInvoices: FeatureBlockContent = {
  title: 'Why businesses use nsave for payroll',
  image: {
    src: '/images/business/invoices.jpg',
    alt: 'A red notebook on grid paper with a pencil, ready for payroll administration',
  },
  rows: [
    {
      icon: 'clock',
      title: 'Instant salary payments',
      description:
        'Salaries reach your employees’ nsave accounts instantly, so your team gets paid reliably and on time.',
    },
    {
      icon: 'globe',
      title: 'Built for international teams',
      description:
        'Pay employees and contractors across multiple markets through one centralised account.',
    },
    {
      icon: 'users',
      title: 'Bulk payroll payments',
      description:
        'Send salaries to multiple employees using their ntag or email address, reducing repetitive manual payments.',
    },
    {
      icon: 'card',
      title: 'More flexibility for employees',
      description:
        'Employees can access virtual card and investment features where available. Availability varies by market.',
    },
  ],
};

export const businessClosing: {
  title: string;
  body: string;
  image: ImageRef;
  primaryCta: LinkItem;
  secondaryCta: LinkItem;
} = {
  title: 'nroll your team today',
  body: 'Apply for an nroll payroll account and pay your international team with $0 nsave transfer fees.',
  image: {
    src: '/images/about/nsave_team_060825-20-1-p-1600.jpg',
    alt: 'The nsave team together in the office',
  },
  primaryCta: { label: 'Apply for Payroll', href: businessApplyHref, external: true },
  secondaryCta: { label: 'Request a demo', href: businessCallHref },
};

export const businessFaq: FaqContent = {
  title: 'Payroll FAQs',
  items: [
    {
      question: "What if my employee isn't on nsave?",
      answer:
        'They can open a free nsave account in minutes using their email. Once their account is ready, you can add them to your payroll payments.',
    },
    {
      question: 'How long does funding take?',
      answer:
        'Funding times depend on the payment rail and currency you use. You will see the expected arrival time before confirming a transfer.',
    },
    {
      question: 'Which countries are supported?',
      answer:
        'You can pay teams in Pakistan, Egypt, Bangladesh and the Gulf, along with other supported markets. Availability depends on where your business and employees are based, so apply or speak with our team to confirm support for your payroll countries.',
    },
    {
      question: 'Is the $200 refundable?',
      answer:
        'Yes. We refund the full $200 once your total payroll transfers through nsave pass $40,000. The fee is not refundable if you do not meet the payroll terms.',
    },
  ],
};
